<template>
    <el-dialog
        ref="DetailDialog"
        :visible="visible"
        :modal-append-to-body="false"
        append-to-body
        class="invoice-e-invoice-dialog ah-detail-dialog"
        width="600px"
        top="5vh"
        :before-close="onClose"
        :close-on-click-modal="false"
        @open="onOpen"
    >
        <template slot="title"> 開立電子發票 </template>
        <div class="detail-row" style="padding-top: 30px">
            <div class="detail-block">
                <el-form ref="form" :model="form" :rules="rules" label-width="145px" label-position="left">
                    <el-form-item prop="incomeItemNo" label="收支項目">
                        <ahSelectVariableStateItem v-model="form.incomeItemNo" :enableType="['invoice']" :show-refresh="false" hide-tip @change="onVariableStateItemChange" />
                    </el-form-item>
                    <el-form-item v-if="isAhSuggestAccountingItemsSelectorShow" prop="accno" label="立帳科目">
                        <ahSuggestAccountingItemsSelector
                            ref="ahSuggestAccountingItemsSelector"
                            v-model="form.accno"
                            @loaded="onAhSuggestAccountingItemsSelectorLoaded"
                        ></ahSuggestAccountingItemsSelector>
                    </el-form-item>
                    <el-form-item v-if="isAhSuggestAccountingItemsSelectorShow" prop="clearanceAccno" label="沖帳科目">
                        <ah-select-accounting-setting v-model="form.clearanceAccno" width="200px" :allowedGroups="['assetsSettings', 'norSettings']" />
                    </el-form-item>

                    <el-form-item v-if="invoice.cny !== 'TWD'" label="匯率">
                        <MoneyInput v-model="form.rat" :precision="4" style="width: 100px" />
                        <span style="margin-left: 10px"> 約當台幣：{{ ntdAmt }} </span>
                    </el-form-item>

                    <hr />
                    <el-form-item prop="invoicePlatform" label="開立平台">
                        <span class="invoice-platform">
                            <a :href="invoicePlatform.href" target="_blank">
                                {{ invoicePlatform.label }}
                            </a>
                        </span>
                    </el-form-item>
                    <el-form-item prop="merchantOrderPostfix">
                        <template v-slot:label>
                            <span>自訂編號 <ah-tip>電子發票平台的紀錄編號</ah-tip></span>
                        </template>
                        <div class="custom-no-input-set">
                            <div class="prefix">
                                <span class="ioo">{{ form.merchantOrderNo }}</span>
                                <span class="spacer">_</span>
                            </div>
                            <el-input v-model="form.merchantOrderPostfix" :maxlength="2" @input="onInputMerchantOrderPostfix" />
                        </div>
                    </el-form-item>
                    <el-form-item prop="buyerName" label="買受人名稱">
                        <el-input v-model="form.buyerName" disabled />
                    </el-form-item>
                    <el-form-item prop="buyerUBN" label="買受人統編">
                        <el-input v-model.trim="form.buyerUBN" disabled />
                    </el-form-item>
                    <el-form-item prop="buyerEmail">
                        <template v-slot:label>
                            <div style="line-height: 1.5">買受人E-mail<br />(電子發票收件信箱)</div>
                        </template>
                        <el-input v-model.trim="form.buyerEmail" :maxlength="50" :show-word-limit="true" />
                    </el-form-item>
                    <el-form-item prop="buyerAddress" label="買受人地址">
                        <el-input v-model.trim="form.buyerAddress" :maxlength="100" :show-word-limit="true" />
                    </el-form-item>
                    <el-form-item prop="comment" label="發票備註">
                        <el-input v-model="form.comment" maxlength="200" show-word-limit></el-input>
                    </el-form-item>
                </el-form>

                <InvoiceItemView :invoiceItems="invoice.invoiceItems" :itemNamTxtLimit="itemNameLimit" />
                <div class="amt-summary">
                    <div class="block">
                        <div class="label">課稅別</div>
                        <div class="data">{{ taxText }}</div>
                    </div>
                    <div>
                        <div class="label">稅額</div>
                        <div class="data">
                            <Money :v="invoice.amT_TAX" round />
                        </div>
                    </div>
                    <div>
                        <div class="label">銷售額</div>
                        <div class="data">
                            <Money :v="netAmount" />
                        </div>
                    </div>
                    <div>
                        <div class="label">含稅總計</div>
                        <div class="data">
                            <Money class="total" :v="invoice.amt" />
                        </div>
                    </div>
                </div>
                <div class="button-set">
                    <el-button @click="onCancel"> 取消 </el-button>
                    <el-button type="primary" :loading="isLoading" @click="onSubmit" :disabled="!isValid">
                        {{ isLoading ? "開立中" : "開立電子發票" }}
                    </el-button>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import _ from 'lodash';
import { isGuiNumberValid } from 'taiwan-id-validator';
import ahSelectVariableStateItem from '@/components/ui/ahSelectVariableStateItem.vue';
import { eInvoicePlatformInfo } from '@/assets/commonData/options';
import ahSuggestAccountingItemsSelector from '@/components/ui/ahSuggestAccountingItemsSelector.vue';
import InvoiceItemView from './InvoiceItemView.vue';
import ahSelectAccountingSetting from '@/components/ui/ahSelectAccountingSetting.vue';

export default {
    props: {
        visible: Boolean,
        invoice: {
            type: Object,
            default: () => {},
        },
        auditProgress: {
            type: Object,
            default: () => {},
        },
    },
    components: {
        ahSelectVariableStateItem,
        InvoiceItemView,
        ahSuggestAccountingItemsSelector,
        ahSelectAccountingSetting,
    },
    computed: {
        invoicePlatform() {
            switch (this.eInvoicePlatform) {
            case 'EZPay':
                return eInvoicePlatformInfo.ezPay;
            case 'SunPay':
                return eInvoicePlatformInfo.sunPay;
            default:
                return eInvoicePlatformInfo.none;
            }
        },
        taxText() {
            switch (this.invoice.tax) {
            case 1:
                return '應稅';
            case 2:
                return '零稅率';
            default:
                return '免稅';
            }
        },
        netAmount() {
            return this.invoice.amt - Math.round(this.invoice.amT_TAX);
        },
        eInvoicePlatform() {
            return this.$store.getters['menu/companyInfo'].eInvoiceInfo?.platform;
        },
        isValid() {
            // 必選收入項目
            if (this.form.incomeItemNo === null) return false;

            // 字數限制檢查
            if (this.isReachItemNameLimit()) return false;

            return true;
        },
        ntdAmt() {
            return Math.round(this.invoice.amt * this.form.rat).toLocaleString('en');
        },
    },
    data() {
        const validateBsn = (rule, value, callback) => {
            if (!isGuiNumberValid(value, true)) {
                callback(new Error('統一編號格式不正確'));
            } else {
                callback();
            }
        };

        return {
            isAhSuggestAccountingItemsSelectorShow: false,

            isLoading: false,
            itemNameLimit: 30,
            suggestAccountItems: [],
            form: {
                incomeItemNo: null,
                accno: null,
                clearanceAccno: null,
                rat: 1,
                // type: 'B2B',
                merchantOrderNo: null,
                buyerName: null,
                buyerUBN: null,
                buyerEmail: null,
                buyerAddress: null,
                comment: null,
                merchantOrderPostfix: '00', // 僅前端使用
            },
            rules: {
                incomeItemNo: [
                    {
                        required: true,
                        message: '請選擇收入項目',
                        trigger: 'change',
                    },
                ],
                // type: [],
                buyerName: [
                    {
                        required: true,
                        message: '請填入買受人名稱',
                        trigger: 'blur',
                    },
                ],
                merchantOrderPostfix: [
                    {
                        required: true,
                        message: '請輸入自訂編號',
                        trigger: 'blur',
                    },
                ],
                buyerUBN: [
                    {
                        required: true,
                        message: '請輸入統一編號',
                        trigger: 'blur',
                    },
                    { validator: validateBsn, trigger: 'blur' },
                ],
                buyerEmail: [],
                buyerAddress: [],
            },
        };
    },
    methods: {
        async onOpen() {
            try {
                this.isAhSuggestAccountingItemsSelectorShow = false;
                this.isLoading = true;
                const VDR = await this.$api({
                    url: 'VendorManagementPage/GetVendorInfo',
                    params: { SNO: this.invoice.vdR_SNO },
                    method: 'GET',
                });

                this.suggestAccountItems = [];
                this.form = {
                    incomeItemNo: this.invoice.incomeItemNo,
                    accno: this.invoice.accno,
                    clearanceAccno: this.invoice.clearanceAccno ? this.invoice.clearanceAccno : '1130',
                    rat: 1,
                    // type: 'B2B',
                    merchantOrderNo: this.invoice.ioo,
                    buyerName: this.invoice.vdR_NAM,
                    buyerUBN: this.invoice.bsn,
                    buyerEmail: null,
                    buyerAddress: VDR.data.item?.adr || null,
                    comment: null,
                    merchantOrderPostfix: '00',
                };

                const email = _.head(VDR.data.item.vendorContacts)?.eml;
                if (typeof email === 'string') {
                    this.form.buyerEmail = email.trim();
                }

                if (this.invoice.cny !== 'TWD') this.form.rat = this.getCnyRat(this.invoice.cny);

                this.$nextTick(() => {
                    console.log(this.invoice.incomeItemNo);
                    if (this.isAhSuggestAccountingItemsSelectorShow) {
                        this.$refs.ahSuggestAccountingItemsSelector.init(this.invoice.incomeItemNo);
                    }
                });
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
            }
        },
        onClose() {
            this.$emit('close');
        },

        onCancel() {
            this.onClose();
        },
        onSubmit() {
            this.$refs.form.validate(async (valid) => {
                if (valid) {
                    try {
                        this.isLoading = true;

                        const items = _.map(this.invoice.invoiceItems, (o) => ({
                            itemName: `${o.iteM_NAM.trim()}`,
                            itemCount: `${o.qty}`,
                            itemUnit: `${o.unit}`,
                            itemPrice: `${Math.round(o.pri * this.form.rat)}`,
                            itemAmt: `${Math.round(o.amt * this.form.rat)}`,
                        }));

                        await this.issueEInvoice(items, this.eInvoicePlatform);

                        this.$emit('reload');
                        this.onClose();
                    } catch (e) {
                        console.log(e);
                    } finally {
                        this.isLoading = false;
                    }
                }
            });
        },
        issueEInvoice(items, platform) {
            let API;
            switch (platform) {
            case 'EZPay':
                API = 'AddEzPayInvoice';
                break;
            case 'SunPay':
                API = 'AddSunPayInvoice';
                break;
            default:
                break;
            }

            const totalAmt = this.invoice.cny === 'TWD' ? `${this.invoice.amt}` : Math.round(this.invoice.amt * this.form.rat);
            const taxAmt = this.invoice.cny === 'TWD' ? `${Math.round(this.invoice.amT_TAX)}` : Math.round((totalAmt / 1.05) * 0.05);
            const nonTaxedAmt = this.invoice.cny === 'TWD' ? `${Math.round(this.netAmount)}` : totalAmt - taxAmt;

            return this.$api({
                url: `/InvoiceManagemnetPage/${API}`,
                method: 'POST',
                data: {
                    auditItem: {
                        invoiceSno: this.invoice.sno,
                        incomeItemNo: this.form.incomeItemNo,
                        accno: this.form.accno,
                        clearanceAccno: this.form.clearanceAccno,
                        auditProgress: { ...this.auditProgress, fin: 1 },
                        rat: this.form.rat,
                    },
                    rat: this.form.rat,
                    ioo: this.invoice.ioo,
                    incomeItemNo: this.form.incomeItemNo,
                    merchantOrderNo: `${this.form.merchantOrderNo}_${this.form.merchantOrderPostfix}`,
                    buyerName: this.form.buyerName.trim(),
                    buyerUBN: this.form.buyerUBN,
                    buyerEmail: this.form.buyerEmail ? this.form.buyerEmail.trim() : '',
                    buyerAddress: this.form.buyerAddress,
                    taxType: `${this.invoice.tax}`,
                    taxRate: this.invoice.tax === 1 ? 5 : 0,
                    totalAmt: `${totalAmt}`,
                    amt: `${nonTaxedAmt}`,
                    taxAmt: `${taxAmt}`,
                    items,
                    comment: this.form.comment,
                },
            });
        },
        isReachItemNameLimit() {
            if (!this.invoice) {
                return false;
            }

            if (!this.invoice.invoiceItems) {
                return false;
            }

            for (let i = 0; i < this.invoice.invoiceItems.length; i += 1) {
                if (this.invoice.invoiceItems[i].iteM_NAM.length > this.itemNameLimit) return true;
            }

            return false;
        },
        onVariableStateItemChange(val) {
            this.isAhSuggestAccountingItemsSelectorShow = true;
            this.$nextTick(() => {
                this.$refs.ahSuggestAccountingItemsSelector.setSuggestAccountItems(val);
            });
        },
        onAhSuggestAccountingItemsSelectorLoaded() {
            this.isAhSuggestAccountingItemsSelectorShow = this.isPayed('G01') || this.$store.getters['account/isSAAdmin'];
        },

        getCnyRat(cny) {
            const cnyOption = this.$store.getters['cny/cnyOptions'].find((x) => x.cny === cny);
            if (cnyOption) return cnyOption.rat;
            return 1;
        },
        onInputMerchantOrderPostfix(value) {
            // 只允許英文與數字，並自動轉為大寫
            const filtered = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            if (filtered !== value) {
                this.form.merchantOrderPostfix = filtered;
            }
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
.invoice-e-invoice-dialog.ah-detail-dialog {
    .invoice-platform a {
        color: $main-color;
        text-decoration: none;
    }
    .amt-summary {
        display: table;
        flex-direction: column;
        margin: 30px 0 30px auto;
        padding-bottom: 20px;
        border-bottom: 1px solid #eee;
        > * {
            display: table-row;
            justify-content: flex-end;

            &:not(:last-child) {
                margin-bottom: 5px;
            }

            > * {
                display: table-cell;
                text-align: right;
            }
            > *:first-child {
                padding-right: 30px;
            }
            .total {
                font-size: 16pt;
            }
        }
    }
    .button-set {
        display: flex;
        justify-content: center;
        margin-top: 40px;
    }

    .custom-no-input-set {
        position: relative;

        > .prefix {
            display: flex;
            position: absolute;
            gap: 2px;
            left: 15px;
            color: #aaa !important;
            font-size: 10.5pt;
            font-family: "Courier", monospace;
            .ioo {
            }
            .spacer {
            }
        }

        .el-input:nth-child(2) {
            width: 170px;
            input {
                font-size: 10.5pt;
                padding-left: 119px;
                background-color: transparent;
                font-family: "Courier", monospace;
            }
        }
    }
}
</style>
